import { z } from 'zod';

export enum FilterOperation {
  Equals = 'Equals',
  NotEquals = 'NotEquals',
  GreaterThan = 'GreaterThan',
  GreaterThanOrEqualTo = 'GreaterThanOrEqualTo',
  LessThan = 'LessThan',
  LessThanOrEqualTo = 'LessThanOrEqualTo',
  Contains = 'Contains',
  DoesNotContain = 'DoesNotContain',
  StartsWith = 'StartsWith',
  EndsWith = 'EndsWith',
  IsOneOf = 'IsOneOf',
  IsNotOneOf = 'IsNotOneOf',
  IsNull = 'IsNull',
  IsNotNull = 'IsNotNull',
}

export enum FieldDataType {
  String = 'String',
  Date = 'Date',
  DateTime = 'DateTime',
  Number = 'Number',
  Boolean = 'Boolean',
  List = 'List',
}

export enum FilterMatchRule {
  All = 'All', // Every condition must match
  Any = 'Any', // At least one condition must match
}

export enum CaseSensitivity {
  ExactMatch = 'ExactMatch',
  IgnoreCase = 'IgnoreCase',
}

export enum SortDirection {
  Ascending = 'Ascending',
  Descending = 'Descending',
}

export enum LimitReturnType {
  List = 'List',
  LimitedList = 'LimitedList',
  FirstObject = 'FirstObject',
}

export const FieldDataTypesWithoutList = Object.values(FieldDataType).filter(
  (type) => type !== FieldDataType.List
);

const ComparableFieldDataTypes = [FieldDataType.Date, FieldDataType.DateTime, FieldDataType.Number];

export const ValuelessFilterOperations = [FilterOperation.IsNull, FilterOperation.IsNotNull];
const ValueFilterOperations = Object.values(FilterOperation).filter(
  (op) => !ValuelessFilterOperations.includes(op)
);

export const MultiValueFilterOperations = [FilterOperation.IsOneOf, FilterOperation.IsNotOneOf];

export const FILTER_OP_SUPPORTED_TYPES: Record<FilterOperation, FieldDataType[]> = {
  [FilterOperation.Equals]: FieldDataTypesWithoutList,
  [FilterOperation.NotEquals]: FieldDataTypesWithoutList,
  [FilterOperation.GreaterThan]: ComparableFieldDataTypes,
  [FilterOperation.GreaterThanOrEqualTo]: ComparableFieldDataTypes,
  [FilterOperation.LessThan]: ComparableFieldDataTypes,
  [FilterOperation.LessThanOrEqualTo]: ComparableFieldDataTypes,
  [FilterOperation.Contains]: [FieldDataType.String, FieldDataType.List],
  [FilterOperation.DoesNotContain]: [FieldDataType.String, FieldDataType.List],
  [FilterOperation.IsOneOf]: [FieldDataType.String, FieldDataType.List, FieldDataType.Number],
  [FilterOperation.IsNotOneOf]: [FieldDataType.String, FieldDataType.List, FieldDataType.Number],
  [FilterOperation.StartsWith]: [FieldDataType.String],
  [FilterOperation.EndsWith]: [FieldDataType.String],
  [FilterOperation.IsNull]: FieldDataTypesWithoutList,
  [FilterOperation.IsNotNull]: FieldDataTypesWithoutList,
};

export const FILTER_TYPE_SUPPORTED_OPS: Record<
  FieldDataType,
  Set<FilterOperation>
> = Object.entries(FieldDataType).reduce(
  (acc, [, dataType]) => {
    acc[dataType] = new Set(
      Object.entries(FILTER_OP_SUPPORTED_TYPES)
        .filter(([, types]) => types.includes(dataType))
        .map(([op]) => op as FilterOperation)
    );
    return acc;
  },
  {} as Record<FieldDataType, Set<FilterOperation>>
);

export interface FilterDataTypeMap {
  [FieldDataType.String]: string;
  [FieldDataType.Date]: Date;
  [FieldDataType.DateTime]: Date;
  [FieldDataType.Number]: number;
  [FieldDataType.Boolean]: boolean;
  [FieldDataType.List]: any[];
}

const BaseFilterConditionSchema = z.object({
  id: z.string().uuid(),
  fieldToFilter: z.string().min(1),
  dataType: z.nativeEnum(FieldDataType),
});

const ValuelessFilterConditionSchema = BaseFilterConditionSchema.extend({
  operation: z.enum(ValuelessFilterOperations as [FilterOperation, ...FilterOperation[]]),
}).strip();

export const CaseSensitivityDataTypes: FieldDataType[] = [FieldDataType.String, FieldDataType.List];

const ValueFilterConditionSchema = BaseFilterConditionSchema.extend({
  caseSensitivity: z.nativeEnum(CaseSensitivity).default(CaseSensitivity.ExactMatch).optional(),
  operation: z.enum(ValueFilterOperations as [FilterOperation, ...FilterOperation[]]),
  value: z.string().min(1),
});

export const FilterConditionSchema = z
  .discriminatedUnion('operation', [ValuelessFilterConditionSchema, ValueFilterConditionSchema])
  .refine(
    (obj) => {
      const supportedTypes = FILTER_OP_SUPPORTED_TYPES[obj.operation];
      return supportedTypes.includes(obj.dataType);
    },
    {
      path: ['operation'],
      message: `Operation not supported for data type`,
    }
  );

export const FilterConfigSchema = z.object({
  filterMatchRule: z.nativeEnum(FilterMatchRule),
  filterConditions: z.array(FilterConditionSchema),
});

export const SortConfigSchema = z.object({
  fieldToSort: z.string(),
  dataType: z.nativeEnum(FieldDataType),
  sortDirection: z.nativeEnum(SortDirection).default(SortDirection.Ascending),
});

export const LimitConfigSchema = z.object({
  limit: z
    .union([z.string(), z.number(), z.null(), z.undefined()])
    .transform((val) => (val === '' || val === null || val === undefined ? undefined : Number(val)))
    .pipe(z.number().min(1).optional())
    .optional(),
  offset: z
    .union([z.string(), z.number(), z.null(), z.undefined()])
    .transform((val) => (val === '' || val === null || val === undefined ? undefined : Number(val)))
    .pipe(z.number().min(0).optional().default(0))
    .default(0),
  returnType: z.nativeEnum(LimitReturnType).default(LimitReturnType.List),
});

export type ValueFilterCondition = z.infer<typeof ValueFilterConditionSchema>;
export type ValuelessFilterCondition = z.infer<typeof ValuelessFilterConditionSchema>;
export type FilterCondition = z.infer<typeof FilterConditionSchema>;
export type FilterConfig = z.infer<typeof FilterConfigSchema>;
export type SortConfig = z.infer<typeof SortConfigSchema>;
export type LimitConfig = z.infer<typeof LimitConfigSchema>;
